import { useCallback, useEffect, useMemo, useRef } from 'react';

import { di } from 'react-magnetic-di';
import { type BoundActions } from 'react-sweet-state';

import { logException } from '@atlassian/conversation-assistant-instrumentation/services/sentry';
import {
	type Source,
	type StreamMessage,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';

import type { MessageResearch, ResearchPlan, ResearchStep } from './MessageResearch';
import type { ChatMessageResearchStore } from './types';
import { useStoreByMessageId } from './useStoreByMessageId';

const ARTIFICIAL_PROGRESS_UPDATE_DELAY = 2000;

const useArtificiallyProgressedSteps = ({
	messageId,
	steps,
	message,
	actions,
}: {
	messageId: string;
	steps: ResearchStep[];
	message?: MessageResearch;
	actions: BoundActions<
		ChatMessageResearchStore,
		{ incrementProgressArtificially: ({ messageId }: { messageId: string }) => () => void }
	>;
}) => {
	// the getArtificiallyProgressingSteps is only executed on state change,
	// so we trigger artificial progress updates from the last state update,
	// the last state update could be from here or from active stream
	useEffect(() => {
		let timeout: ReturnType<typeof setTimeout> | undefined;
		if (message?.currentProgress && !message?.researchCompleted && !message?.errored) {
			timeout = setTimeout(() => {
				actions.incrementProgressArtificially({ messageId });
			}, ARTIFICIAL_PROGRESS_UPDATE_DELAY);
		}
		if (message?.researchCompleted || message?.errored) {
			clearTimeout(timeout);
		}
		return () => {
			clearTimeout(timeout);
		};
	}, [message?.currentProgress, message?.errored, actions, messageId, message?.researchCompleted]);

	// return only the ordered steps that are completed or have their predecessors completed
	const stepToCompletionTime = useRef<{ [key: string]: number }>({});
	// Stagger reveals by MIN_WAIT_THRESHOLD so steps don't all pop at once during live streaming.
	// When the store already marks a step completed (including reconnect/replay), skip the wait
	// so checkmarks appear immediately instead of waiting 200ms after the previous step.
	const MIN_WAIT_THRESHOLD = 200;
	const artificiallyProgressingSteps = useMemo(
		() => {
			const currentStepsTraversalTime = Date.now();
			try {
				const mappedSteps = steps
					.sort((a, b) => a.order - b.order)
					.reduce(
						(accumulator, step, currentIndex, array) => {
							const allPreviousCompleted =
								currentIndex > 0
									? accumulator[currentIndex - 1].allPreviousAndCurrentCompleted
									: true;
							const timeSincePreviousCompleted =
								currentIndex > 0
									? currentStepsTraversalTime -
										stepToCompletionTime.current[array[currentIndex - 1].stepId]
									: MIN_WAIT_THRESHOLD;
							const canRevealCompletedStep =
								timeSincePreviousCompleted >= MIN_WAIT_THRESHOLD || step.completed;
							if (
								message?.researchCompleted ||
								(allPreviousCompleted && step.completed && canRevealCompletedStep)
							) {
								if (!stepToCompletionTime.current[step.stepId]) {
									stepToCompletionTime.current[step.stepId] = currentStepsTraversalTime;
								}
							} else {
								delete stepToCompletionTime.current[step.stepId];
							}

							const currentCompleted = step.stepId in stepToCompletionTime.current;
							// Logic that uses both currentValue and accumulator
							accumulator.push({
								step: { ...step, completed: allPreviousCompleted && currentCompleted },
								allPreviousAndCurrentCompleted: allPreviousCompleted && currentCompleted,
							});
							return accumulator;
						},
						[] as { step: ResearchStep; allPreviousAndCurrentCompleted: boolean }[],
					);

				// Show all arrived steps with a title. Sliding window capping
				// is applied at the UI layer for the collapsed view only, so the
				// expanded view can show all steps.
				return mappedSteps
					.filter((mappedStep) => {
						if (mappedStep.step.immediate) {
							return true;
						}
						return !!mappedStep.step.title;
					})
					.map((mappedStep) => mappedStep.step);
			} catch {
				// fallback in case of an unexpected error
				logException({
					exception: new Error(`Failed to generate artificiallyProgressingSteps`),
					name: 'MessageResearchArtificialStepProgressFailed',
				});
				return steps;
			}
		},
		// current progress is not used in memo
		// but that is what is intended to evaluate the stream again to validate time since last completed
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[steps, message?.researchCompleted, message?.currentProgress],
	);
	return { artificiallyProgressingSteps };
};

export const useChatMessageResearchById = ({
	messageId,
}: {
	messageId: string;
}): {
	getSteps: () => ResearchStep[];
	artificiallyProgressingSteps: ResearchStep[];
	realtimeProgressingSteps: ResearchStep[];
	accumulatedSources: Source[];
	isRoundComplete: (roundId: number) => boolean;
	isPreviousRoundComplete: (roundId: number) => boolean;
	isNextRoundComplete: (roundId: number) => boolean;
	isLastRound: (roundId: number) => boolean;
	resetHasReceivedAnswerPart: (args_0: { messageId: string }) => void;
	addImplicitResearchPlan: (args_0: {
		messageId: string;
		content: string;
		roundId: number;
	}) => void;
	processStreamResearch: (args_0: {
		messageId: string;
		streamMessage: StreamMessage;
		isDeepResearch?: boolean;
	}) => void;
	updateResearchMessageId: (args_0: { messageId: string; newMessageId: string }) => void;
	incrementProgressArtificially: (args_0: { messageId: string }) => void;
	markIncompleteResearchesErrored: (args_0: { messageId: string }) => void;
	completeAllSteps: (args_0: { messageId: string }) => void;
	getMessageResearchById: (args_0: { messageId: string }) => MessageResearch | undefined;
	clearMessageResearch: (args_0: { messageId: string }) => void;
	steps?:
		| {
				[stepId: string]: ResearchStep;
		  }
		| undefined;
	researchPlans?:
		| {
				[roundId: number]: ResearchPlan;
		  }
		| undefined;
	researchCompleted?: boolean;
	hasReceivedAnswerPart?: boolean;
	currentProgress?: number;
	errored?: boolean;
	isDeepResearch?: boolean;
	hasThinkDeeperTriggered?: boolean;
} => {
	di(useStoreByMessageId);

	const [state, actions] = useStoreByMessageId(messageId);

	const steps = useMemo(() => {
		return state?.steps ? Object.values(state.steps) : [];
	}, [state?.steps]);

	const getSteps = useCallback((): ResearchStep[] => steps, [steps]);
	const accumulatedSources = useMemo(() => {
		const seen = new Set();
		return Object.values(steps)
			.map((s) => s.sources)
			.flat()
			.filter((source) => !!source?.url)
			.filter((source) => {
				if (seen.has(source.ari)) {
					return false;
				}
				seen.add(source.ari);
				return true;
			});
	}, [steps]);

	const isLastRound = useCallback(
		(roundId: number): boolean => {
			if (steps.filter((step) => step.roundId === roundId).length === steps.length) {
				return true;
			}
			const uniqueRoundIds = [...new Set(steps.map((step) => step.roundId))].sort((a, b) => a - b);
			return uniqueRoundIds.length > 0 && roundId === uniqueRoundIds[uniqueRoundIds.length - 1];
		},
		[steps],
	);

	const isRoundComplete = useCallback(
		(roundId: number): boolean => {
			return steps.filter((step) => step.roundId === roundId)?.every((step) => step.completed);
		},
		[steps],
	);

	const isPreviousRoundComplete = useCallback(
		(roundId: number): boolean => {
			return steps.filter((step) => step.roundId === roundId - 1)?.every((step) => step.completed);
		},
		[steps],
	);

	const isNextRoundComplete = useCallback(
		(roundId: number): boolean => {
			const nextRoundSteps = steps.filter((step) => step.roundId === roundId + 1);

			if (nextRoundSteps.length === 0) {
				return true;
			}
			return nextRoundSteps.every((step) => step.completed);
		},
		[steps],
	);

	const { artificiallyProgressingSteps } = useArtificiallyProgressedSteps({
		messageId,
		steps,
		message: state,
		actions,
	});

	return {
		...state,
		...actions,
		getSteps,
		artificiallyProgressingSteps,
		realtimeProgressingSteps: steps,
		accumulatedSources,
		isRoundComplete,
		isPreviousRoundComplete,
		isNextRoundComplete,
		isLastRound,
	};
};
